import { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    FormControl,
    FormControlLabel,
    InputLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import useGetAssociatedInvoices from '../../hooks/useGetAssociatedInvoices';
import usePatchCreditMemoMode from '../../hooks/ProcessedTimesheets/usePatchCreditMemoMode';
import { openInvoicePdf } from '../../Services/InvoiceService';
import { COLORS } from '../../../utils/Application_Constants';

function InvoiceLink({ invoice, onClick }) {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
        setIsLoading(true);
        await onClick();
        setIsLoading(false);
    };

    return (
        <>
            {' '}
            <Typography
                variant="body2"
                sx={{
                    textDecoration: 'underline',
                    color: COLORS.LT_JOURNEY,
                    display: 'inline',
                    cursor: 'pointer',
                    pointerEvents: isLoading ? 'none' : 'auto',
                }}
                onClick={isLoading ? null : () => handleClick()}
            >
                {invoice.invoiceNumber}
            </Typography>
        </>
    );
}

function InvoiceReviewResolutionForm({ id, onClose, onSuccess }) {
    const [creditMemoMode, setCreditMemoMode] = useState(null);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const queryClient = useQueryClient();
    const { isLoading: associatedInvoicesLoading, data: associatedInvoicesData } =
        useGetAssociatedInvoices({
            id,
        });

    const handleOpenPdf = async (invoiceId) => {
        try {
            setIsPdfLoading(true);
            await openInvoicePdf(invoiceId);
        } catch (error) {
            console.error('Error opening PDF:', error);
        } finally {
            setIsPdfLoading(false);
        }
    };
    const invoices = associatedInvoicesData?.associatedInvoices || [];

    const { mutate: patchCreditMemoMode, isLoading: isPatchLoading } = usePatchCreditMemoMode({
        onSuccess: () => {
            onSuccess('Resolved');

            // Invalidate processed timesheets lists, processed timesheet details, and activity feed            queryClient.invalidateQueries({ queryKey: ['processedTimesheets'] });
            queryClient.invalidateQueries({
                queryKey: ['processedTimesheetDetails', { processedTimesheetId: id.toString() }],
            });
            queryClient.invalidateQueries({
                queryKey: ['getActivityFeed', { processedTimesheetId: id.toString() }],
            });
        },
        onError: (error) => {
            console.error('Error patching credit memo mode:', error);
            setSubmitError('There was an error submitting your request. Please try again.');
        },
    });

    const handleSubmit = async () => {
        patchCreditMemoMode({
            processedTimesheetId: id,
            creditMemoMode,
            invoiceIds: associatedInvoicesData?.associatedInvoices?.map((invoice) => invoice.id),
        });
    };

    return (
        <Box display="flex" flexDirection="column">
            <Typography variant="h5" sx={{ mb: 3 }}>
                Processing Alert
            </Typography>
            {!associatedInvoicesLoading && associatedInvoicesData ? (
                <>
                    {associatedInvoicesData?.associatedInvoices.length === 0 ? (
                        <Typography variant="body2">
                            This PTS is not linked to any invoices.
                        </Typography>
                    ) : (
                        <Typography variant="body2" sx={{ float: 'left' }}>
                            This PTS is linked to these invoices:{' '}
                            {invoices.length > 0 &&
                                invoices.map((invoice) => (
                                    <Typography
                                        key={invoice.id}
                                        sx={{
                                            display: 'inline-block',
                                            '&:not(:last-child)::after': { content: '",\u00A0"' },
                                        }}
                                    >
                                        <InvoiceLink
                                            invoice={invoice}
                                            onClick={() => handleOpenPdf(invoice.id)}
                                        />
                                    </Typography>
                                ))}
                        </Typography>
                    )}
                    <FormControl sx={{ mt: 3 }}>
                        <InputLabel
                            shrink
                            htmlFor="haveReferenceInTwoYear"
                            sx={{
                                fontSize: '14px',
                                '& .MuiInputLabel-asterisk': {
                                    color: COLORS.LT_PEPPER,
                                },
                            }}
                            required
                        >
                            Select what type of credit you need to do
                        </InputLabel>
                        <div>
                            <RadioGroup
                                row
                                aria-label="completed"
                                name="creditMemoMode"
                                onChange={(e) => setCreditMemoMode(e.target.value)}
                            >
                                <FormControlLabel
                                    value="Partial"
                                    control={
                                        <Radio
                                            color="primary"
                                            style={{ color: COLORS.RAVEN }}
                                            name="creditMemoMode"
                                            disableRipple
                                            size="small"
                                        />
                                    }
                                    label={
                                        <span
                                            style={{
                                                fontSize: '14px',
                                            }}
                                        >
                                            Partial
                                        </span>
                                    }
                                />
                                <FormControlLabel
                                    value="Full"
                                    control={
                                        <Radio
                                            color="primary"
                                            style={{ color: COLORS.RAVEN }}
                                            name="creditMemoMode"
                                            disableRipple
                                            size="small"
                                        />
                                    }
                                    label={
                                        <span
                                            style={{
                                                fontSize: '14px',
                                            }}
                                        >
                                            Full Credit
                                        </span>
                                    }
                                />
                            </RadioGroup>
                        </div>
                        <DialogActions sx={{ justifyContent: 'flex-start', padding: 0, mt: 5 }}>
                            <Button
                                disabled={isPatchLoading}
                                color="primary"
                                onClick={onClose}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                loading={isPatchLoading}
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                            >
                                Confirmed
                            </Button>
                        </DialogActions>
                        {submitError && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                {submitError}
                            </Typography>
                        )}
                    </FormControl>
                    {isPdfLoading && (
                        <Box sx={{ position: 'absolute', bottom: 20, right: 20 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{ py: 2, w: '100%', textAlign: 'center' }}>
                    <CircularProgress sx={{ margin: 'auto' }} />
                </Box>
            )}
        </Box>
    );
}

export default InvoiceReviewResolutionForm;
